import { ACCOUNT_TYPE_TO_VIEWABLE_ACCOUNT } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/EntityFields/constants";
import { getProfileName } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/utils";
import type { ProfileData } from "./types";

export const formatAddress = (
  addresses: ProfileData["addresses"] | null | undefined,
): string => {
  if (!addresses || addresses.length === 0) {
    return "--";
  }

  const primaryAddress = addresses.find((addr) => addr.isPrimaryDomicile) || addresses[0];

  if (!primaryAddress) {
    return "--";
  }

  const parts = [
    primaryAddress.street,
    primaryAddress.street2,
    primaryAddress.city,
    primaryAddress.state?.name || primaryAddress.province,
    primaryAddress.postalCode,
    primaryAddress.country?.name,
  ].filter(Boolean);

  return parts.join(", ");
};

export const formatOwners = (
  beneficialOwners: ProfileData["beneficialOwners"] | null | undefined,
): string => {
  if (!beneficialOwners || beneficialOwners.length === 0) {
    return "--";
  }

  return beneficialOwners
    .map((owner) => {
      const name = owner.childProfile ? getProfileName(owner.childProfile) : "--";
      const percentage = owner.percentOwnership ? ` (${owner.percentOwnership}%)` : "";
      return `${name}${percentage}`;
    })
    .join(", ");
};

export const formatControllers = (
  controllingPersons: ProfileData["controllingPersons"] | null | undefined,
): string => {
  if (!controllingPersons || controllingPersons.length === 0) {
    return "--";
  }

  return controllingPersons
    .map((controller) => {
      const name = controller.childProfile ? getProfileName(controller.childProfile) : "--";
      const title = controller.title ? ` (${controller.title})` : "";
      return `${name}${title}`;
    })
    .join(", ");
};

export const getEntityType = (accountType: string | null | undefined): string => {
  if (!accountType) {
    return "--";
  }

  return ACCOUNT_TYPE_TO_VIEWABLE_ACCOUNT[accountType.toLowerCase()] || accountType;
};

export const getMetadataValue = (metaData: unknown, key: string): string | boolean => {
  if (!metaData || typeof metaData !== "object") {
    return "--";
  }

  const data = metaData as Record<string, unknown>;
  const value = data[key];

  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    return value;
  }

  return "--";
};

export const formatBooleanValue = (value: string | boolean): string => {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (value === "true" || value === "Yes") {
    return "Yes";
  }

  if (value === "false" || value === "No") {
    return "No";
  }

  return value;
};

export const formatRiskRating = (
  riskRating?: { overriddenRating?: string | null } | null,
): string => {
  if (!riskRating?.overriddenRating) return "--";

  const rating = riskRating.overriddenRating;
  // Capitalize first letter
  return rating.charAt(0).toUpperCase() + rating.slice(1).toLowerCase();
};

/**
 * Escape special regex characters in a string.
 */
const escapeRegExp = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

/**
 * Replace the profile name in the question with "this entity",
 * so as to provide a generic label for the profile.
 */
export const formatQuestionText = (
  questionText: string,
  profileName: string | null | undefined,
): string => {
  if (!profileName || !questionText) return questionText || "";
  const escapedName = escapeRegExp(profileName);
  return questionText.replace(new RegExp(escapedName, "gi"), "this entity");
};

const stableStringify = (value: unknown): string => {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(",")}}`;
};

/**
 * Signature of a document's mutable metadata fields (regular metadata,
 * taxReportingMetadata, and FATCA/CRS taxDataSourceAssignments). Excludes
 * identity fields (name, documentPurpose) so two same-name documents whose
 * metadata or assignments differ produce different signatures.
 */
export const createDocumentMetaDataSignature = (doc: ProfileData["documents"][0]): string => {
  const metadata = (doc.metadata || [])
    .map((m) => `${m.key}:${m.value || ""}`)
    .sort()
    .join("|");
  const taxReportingMetadata = (doc.taxReportingMetadata || [])
    .map((t) => `${t.taxReportingType}:${stableStringify(t.metaData)}`)
    .sort()
    .join("|");
  const taxDataSourceAssignments = (doc.taxDataSourceAssignments || [])
    .map((a) => `${a.assignmentType}:${a.referencedCaseId}:${a.status}`)
    .sort()
    .join("|");
  return JSON.stringify({ metadata, taxReportingMetadata, taxDataSourceAssignments });
};

const getDocumentMatchKey = (doc: ProfileData["documents"][0]): string =>
  `${doc.documentPurpose?.baseType || "other"}|${doc.name}`;

/**
 * Pair each proposed document with its matching current document by
 * (baseType, name) and flag those whose metadata or tax data source
 * assignments changed. Proposed documents without a matching current document
 * (i.e., newly added) are not flagged — those follow the add/remove path.
 *
 * Returns a Map keyed by proposed doc id; the boolean is true when the matched
 * pair has a metadata or assignment diff.
 */
export const computeDocumentChangeFlags = (
  current: ProfileData["documents"] | null | undefined,
  proposed: ProfileData["documents"] | null | undefined,
): Map<number, boolean> => {
  const flags = new Map<number, boolean>();
  if (!proposed?.length) return flags;

  const currentByKey = new Map<string, ProfileData["documents"][0]>();
  (current || []).forEach((doc) => {
    const key = getDocumentMatchKey(doc);
    if (!currentByKey.has(key)) currentByKey.set(key, doc);
  });

  proposed.forEach((proposedDoc) => {
    const matched = currentByKey.get(getDocumentMatchKey(proposedDoc));
    if (!matched) {
      flags.set(proposedDoc.id, false);
      return;
    }
    flags.set(
      proposedDoc.id,
      createDocumentMetaDataSignature(matched) !== createDocumentMetaDataSignature(proposedDoc),
    );
  });

  return flags;
};

// Comparison utilities to check if values are different
export const isDifferent = {
  string: (
    current: string | null | undefined,
    proposed: string | null | undefined,
  ): boolean => {
    const currentVal = current || "";
    const proposedVal = proposed || "";
    return currentVal !== proposedVal;
  },

  date: (current: string | null | undefined, proposed: string | null | undefined): boolean => {
    const currentVal = current || "";
    const proposedVal = proposed || "";
    return currentVal !== proposedVal;
  },

  boolean: (current: string | boolean, proposed: string | boolean): boolean => {
    const currentVal = formatBooleanValue(current);
    const proposedVal = formatBooleanValue(proposed);
    return currentVal !== proposedVal;
  },

  riskRating: (
    current: { overriddenRating?: string | null } | null | undefined,
    proposed: { overriddenRating?: string | null } | null | undefined,
  ): boolean => {
    const currentVal = formatRiskRating(current);
    const proposedVal = formatRiskRating(proposed);
    return currentVal !== proposedVal;
  },

  relationships: <T extends { childProfile: { id: number; sourceProfileId?: number | null } }>(
    current: T[] | null | undefined,
    proposed: T[] | null | undefined,
    getKey: (item: T) => string,
    currentProfileId?: number,
    proposedProfileId?: number,
  ): boolean => {
    if (!current && !proposed) return false;
    if (!current || !proposed) return true;
    if (current.length !== proposed.length) return true;

    const currentSet = new Set(
      current.map((item) => {
        const profileId = currentProfileId ?? item.childProfile.id;
        return getKey({ ...item, childProfile: { ...item.childProfile, id: profileId } });
      }),
    );

    const proposedSet = new Set(
      proposed.map((item) => {
        const profileId = proposedProfileId ?? item.childProfile.sourceProfileId;
        return getKey({ ...item, childProfile: { ...item.childProfile, id: profileId } });
      }),
    );

    if (currentSet.size !== proposedSet.size) return true;
    for (const item of currentSet) {
      if (!proposedSet.has(item)) return true;
    }
    return false;
  },

  owners: (
    current: ProfileData["beneficialOwners"],
    proposed: ProfileData["beneficialOwners"],
    currentProfileId?: number,
    proposedProfileId?: number,
  ): boolean =>
    isDifferent.relationships(
      current,
      proposed,
      (o) => `${o.childProfile.id}:${o.percentOwnership || ""}`,
      currentProfileId,
      proposedProfileId,
    ),

  controllers: (
    current: ProfileData["controllingPersons"],
    proposed: ProfileData["controllingPersons"],
    currentProfileId?: number,
    proposedProfileId?: number,
  ): boolean =>
    isDifferent.relationships(
      current,
      proposed,
      (c) => `${c.childProfile.id}:${c.title || ""}`,
      currentProfileId,
      proposedProfileId,
    ),

  documents: (
    current: ProfileData["documents"],
    proposed: ProfileData["documents"],
  ): { isDifferent: boolean; differentBaseTypes: Set<string> } => {
    const createDocumentSignature = (doc: ProfileData["documents"][0]): string => {
      const purpose = doc.documentPurpose
        ? {
            baseType: doc.documentPurpose.baseType || "",
            expires: doc.documentPurpose.expires || false,
            complianceObjectType: doc.documentPurpose.complianceObjectType || "",
          }
        : {};
      return JSON.stringify({
        name: doc.name || "",
        purpose,
        metaData: createDocumentMetaDataSignature(doc),
      });
    };

    // Group documents by baseType with comprehensive signatures
    const groupByBaseType = (docs: ProfileData["documents"]) => {
      const grouped = new Map<string, Set<string>>();
      docs?.forEach((doc) => {
        const baseType = doc.documentPurpose?.baseType || "other";
        if (!grouped.has(baseType)) {
          grouped.set(baseType, new Set());
        }
        grouped.get(baseType)?.add(createDocumentSignature(doc));
      });
      return grouped;
    };

    const currentGrouped = groupByBaseType(current);
    const proposedGrouped = groupByBaseType(proposed);

    const allBaseTypes = new Set([...currentGrouped.keys(), ...proposedGrouped.keys()]);

    const differentBaseTypes = new Set<string>();
    let isDifferent = false;

    allBaseTypes.forEach((baseType) => {
      const currentSignatures = currentGrouped.get(baseType) || new Set();
      const proposedSignatures = proposedGrouped.get(baseType) || new Set();

      // Check if the sets of document signatures are different
      if (currentSignatures.size !== proposedSignatures.size) {
        differentBaseTypes.add(baseType);
        isDifferent = true;
        return;
      }

      for (const signature of currentSignatures) {
        if (!proposedSignatures.has(signature)) {
          differentBaseTypes.add(baseType);
          isDifferent = true;
          return;
        }
      }
    });

    return { isDifferent, differentBaseTypes };
  },
};
